const crcTable = new Uint32Array(256).map((_, index) => {
    let crc = index;

    for (let bitIndex = 0; bitIndex < 8; bitIndex += 1) {
        const shouldApplyPolynomial = (crc & 1) === 1;
        crc >>>= 1;

        if (shouldApplyPolynomial) {
            crc ^= 0xedb88320;
        }
    }

    return crc >>> 0;
});

function calculateCrc32(bytes) {
    let crc = 0xffffffff;

    for (const value of bytes) {
        crc = crcTable[(crc ^ value) & 0xff] ^ (crc >>> 8);
    }

    return (crc ^ 0xffffffff) >>> 0;
}

function getDosDateTime(date = new Date()) {
    const year = Math.max(date.getFullYear(), 1980);
    const dosTime =
        ((date.getHours() & 0x1f) << 11) |
        ((date.getMinutes() & 0x3f) << 5) |
        Math.floor((date.getSeconds() & 0x3f) / 2);
    const dosDate =
        (((year - 1980) & 0x7f) << 9) |
        (((date.getMonth() + 1) & 0x0f) << 5) |
        (date.getDate() & 0x1f);

    return { dosDate, dosTime };
}

function writeUint16(target, offset, value) {
    target[offset] = value & 0xff;
    target[offset + 1] = (value >>> 8) & 0xff;
}

function writeUint32(target, offset, value) {
    target[offset] = value & 0xff;
    target[offset + 1] = (value >>> 8) & 0xff;
    target[offset + 2] = (value >>> 16) & 0xff;
    target[offset + 3] = (value >>> 24) & 0xff;
}

function createLocalFileHeader(fileNameBytes, fileData, crc32, dosDate, dosTime) {
    const header = new Uint8Array(30 + fileNameBytes.length);

    writeUint32(header, 0, 0x04034b50);
    writeUint16(header, 4, 20);
    writeUint16(header, 6, 0);
    writeUint16(header, 8, 0);
    writeUint16(header, 10, dosTime);
    writeUint16(header, 12, dosDate);
    writeUint32(header, 14, crc32);
    writeUint32(header, 18, fileData.length);
    writeUint32(header, 22, fileData.length);
    writeUint16(header, 26, fileNameBytes.length);
    writeUint16(header, 28, 0);
    header.set(fileNameBytes, 30);

    return header;
}

function createCentralDirectoryHeader(fileNameBytes, fileData, crc32, dosDate, dosTime, localHeaderOffset) {
    const header = new Uint8Array(46 + fileNameBytes.length);

    writeUint32(header, 0, 0x02014b50);
    writeUint16(header, 4, 20);
    writeUint16(header, 6, 20);
    writeUint16(header, 8, 0);
    writeUint16(header, 10, 0);
    writeUint16(header, 12, dosTime);
    writeUint16(header, 14, dosDate);
    writeUint32(header, 16, crc32);
    writeUint32(header, 20, fileData.length);
    writeUint32(header, 24, fileData.length);
    writeUint16(header, 28, fileNameBytes.length);
    writeUint16(header, 30, 0);
    writeUint16(header, 32, 0);
    writeUint16(header, 34, 0);
    writeUint16(header, 36, 0);
    writeUint32(header, 38, 0);
    writeUint32(header, 42, localHeaderOffset);
    header.set(fileNameBytes, 46);

    return header;
}

function createEndOfCentralDirectory(entryCount, centralDirectorySize, centralDirectoryOffset) {
    const footer = new Uint8Array(22);

    writeUint32(footer, 0, 0x06054b50);
    writeUint16(footer, 4, 0);
    writeUint16(footer, 6, 0);
    writeUint16(footer, 8, entryCount);
    writeUint16(footer, 10, entryCount);
    writeUint32(footer, 12, centralDirectorySize);
    writeUint32(footer, 16, centralDirectoryOffset);
    writeUint16(footer, 20, 0);

    return footer;
}

export async function createZipBlob(files) {
    const zipParts = [];
    const centralDirectoryParts = [];
    const encoder = new TextEncoder();
    let localHeaderOffset = 0;

    for (const file of files) {
        const fileNameBytes = encoder.encode(file.name);
        const fileBytes = new Uint8Array(await file.blob.arrayBuffer());
        const crc32 = calculateCrc32(fileBytes);
        const { dosDate, dosTime } = getDosDateTime();

        const localHeader = createLocalFileHeader(fileNameBytes, fileBytes, crc32, dosDate, dosTime);
        const centralDirectoryHeader = createCentralDirectoryHeader(
            fileNameBytes,
            fileBytes,
            crc32,
            dosDate,
            dosTime,
            localHeaderOffset,
        );

        zipParts.push(localHeader, fileBytes);
        centralDirectoryParts.push(centralDirectoryHeader);
        localHeaderOffset += localHeader.length + fileBytes.length;
    }

    const centralDirectorySize = centralDirectoryParts.reduce((size, part) => size + part.length, 0);
    const endOfCentralDirectory = createEndOfCentralDirectory(
        files.length,
        centralDirectorySize,
        localHeaderOffset,
    );

    return new Blob([...zipParts, ...centralDirectoryParts, endOfCentralDirectory], { type: 'application/zip' });
}
